import * as Metrics from 'metrics/Metrics';
import {MultidocOutputTableMap} from 'notebook/tables/excess/EvaluationConstants';
import {isInvalidCellError} from 'notebook/tables/excess/Util';
import {tableCellCountBucket} from 'notebook/util/analytics/TableAnalyticsUtils';

export interface ErrorStats {
  totalCells: number;
  totalErrors: number;
  errorsByType: Record<string, number>;
}

/**
 * Collects error statistics from an evaluated table output
 *
 * @param output The output table map from evaluation
 * @returns Statistics including total cells, total errors, and breakdown by error type
 */
export function collectErrorStats(output: MultidocOutputTableMap): ErrorStats {
  let totalCells = 0;
  let totalErrors = 0;
  const errorsByType: Record<string, number> = {};

  Object.values(output).forEach((docTables) => {
    Object.values(docTables).forEach((table) => {
      table.forEach((row) => {
        row.forEach((cell) => {
          totalCells++;

          if (cell.error) {
            const errors = Array.isArray(cell.error) ? cell.error : [cell.error];

            errors.forEach((error) => {
              if (isInvalidCellError(error)) {
                totalErrors++;
                errorsByType[error.type] = (errorsByType[error.type] || 0) + 1;
              }
            });
          }
        });
      });
    });
  });

  return {totalCells, totalErrors, errorsByType};
}

/**
 * Tracks metrics for formula evaluation errors
 * Increments a count metric per error type found
 */
export function trackErrorMetrics(output: MultidocOutputTableMap, docType: string): void {
  const stats = collectErrorStats(output);

  if (stats.totalCells === 0) {
    return;
  }

  // Track a count metric for each error type found
  Object.entries(stats.errorsByType).forEach(([errorType, count]) => {
    Metrics.count('table.eval_errors', count, {
      error_type: errorType,
      doc_type: docType,
      cell_count: tableCellCountBucket(stats.totalCells),
    });
  });
}
